/**
 * Encaminhar chamado no Board — UI de programação (Open OS v2 agenda).
 * Mantém a lógica de encaminhamento do App.js via onConfirm.
 */

import React, { useEffect, useState } from 'react';
import OpenOsV2MockOverlay from '../../new-os/components/OpenOsV2MockOverlay';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getBoardEncaminharScheduleOption } from '../utils/boardEncaminharChamadoUtils';
import {
  getBoardOrderRouteHelpAddress,
  getBoardOrderRouteHelpCity,
} from '../utils/boardEncaminharRouteUtils';
import './BoardEncaminharAgendaOverlay.css';

/** Acima de order-details-v2-overlay (26000) e odv2-tooltip (27000). */
const BOARD_ENCAMINHAR_AGENDA_Z_INDEX = 28000;

/**
 * @param {{
 *   open: boolean,
 *   excludeTechnicianName?: string,
 *   orderIds?: string[],
 *   scheduleOriginAddress?: string,
 *   scheduleOriginCity?: string,
 *   hideApenasTecnico?: boolean,
 *   overlayZIndex?: number,
 *   onClose: () => void,
 *   onConfirm: (payload: {
 *     technicianName: string,
 *     technicianId?: string | number | null,
 *     scheduleType: string,
 *     groupName: string,
 *     scheduleDate?: string | null,
 *     semPrevisao?: boolean,
 *     standbyNotes?: string | null,
 *   }) => void | Promise<void>,
 * }} props
 */
export function BoardEncaminharAgendaOverlay({
  open,
  excludeTechnicianName = '',
  orderIds = [],
  scheduleOriginAddress = '',
  scheduleOriginCity = '',
  hideApenasTecnico = false,
  overlayZIndex = BOARD_ENCAMINHAR_AGENDA_Z_INDEX,
  onClose,
  onConfirm,
}) {
  const [confirming, setConfirming] = useState(false);
  const [fetchedOrigin, setFetchedOrigin] = useState({ address: '', city: '' });

  useEffect(() => {
    if (!open) {
      setFetchedOrigin({ address: '', city: '' });
      return undefined;
    }
    const cachedAddress = String(scheduleOriginAddress || '').trim();
    if (cachedAddress) {
      setFetchedOrigin({
        address: cachedAddress,
        city: String(scheduleOriginCity || '').trim(),
      });
      return undefined;
    }
    const id = String(orderIds?.[0] ?? '').trim();
    if (!id) {
      setFetchedOrigin({ address: '', city: '' });
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(
          `${getApiBaseUrl()}/api/orders/lookup-for-modal/${encodeURIComponent(id)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        const json = await res.json();
        if (cancelled || !json?.success || !json.data) return;
        setFetchedOrigin({
          address: getBoardOrderRouteHelpAddress(json.data),
          city: getBoardOrderRouteHelpCity(json.data),
        });
      } catch {
        if (!cancelled) setFetchedOrigin({ address: '', city: '' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, orderIds, scheduleOriginAddress, scheduleOriginCity]);

  const resolvedAddress =
    String(scheduleOriginAddress || '').trim() || fetchedOrigin.address;
  const resolvedCity =
    String(scheduleOriginCity || '').trim() || fetchedOrigin.city;

  const handleAgendaConfirm = async (agenda) => {
    const scheduleType = agenda?.scheduleType;
    if (hideApenasTecnico && scheduleType === 'apenas_tecnico') return;
    const meta = getBoardEncaminharScheduleOption(scheduleType);
    if (!meta || !agenda?.tecnico) return;
    setConfirming(true);
    try {
      await onConfirm?.({
        technicianName: agenda.tecnico,
        technicianId: agenda.tecnicoObj?.id_tech ?? agenda.tecnicoObj?.id ?? agenda.tecnicoId ?? null,
        scheduleType: meta.id,
        groupName: meta.groupName,
        scheduleDate: agenda.dataIso || null,
        semPrevisao: !!agenda.noCertainDate,
        standbyNotes: agenda.standbyNotes || null,
      });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <OpenOsV2MockOverlay
      isOpen={open}
      onClose={onClose}
      agendaOnly
      agendaDialogTitle="Programar atendimento"
      overlayClassName="board-encaminhar-agenda-overlay"
      overlayZIndex={overlayZIndex}
      excludeTechnicianName={excludeTechnicianName}
      scheduleOriginAddress={resolvedAddress}
      scheduleOriginCity={resolvedCity}
      hideApenasTecnico={hideApenasTecnico}
      onAgendaConfirm={handleAgendaConfirm}
      agendaConfirming={confirming}
    />
  );
}

export default BoardEncaminharAgendaOverlay;
